import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Tent, LogIn } from 'lucide-react'

export default function LoginPage() {
  const { session, signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (session) return <Navigate to="/dashboard" replace />

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)

    const { error } = await signIn(email, password)

    if (error) {
      setError(error.message)
      setLoading(false)
    }
  }

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-card">
        {/* Brand */}
        <div className="login-brand">
          <div className="login-logo">
            <Tent size={28} />
          </div>
          <div>
            <h1 className="login-title">Winnipeg Tent Rental</h1>
            <p className="login-subtitle">CRM & Booking System</p>
          </div>
        </div>

        {/* Form */}
        <form className="login-form" onSubmit={handleSubmit}>
          <h2 className="login-heading">Sign in to your account</h2>

          {error && <div className="alert alert-error" data-testid="feedback-banner-error">{error}</div>}

          <div className="form-group">
            <label className="form-label" htmlFor="email">Email address</label>
            <input
              id="email"
              data-testid="login-email-input"
              type="email"
              className="form-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Password</label>
            <input
              id="password"
              data-testid="login-password-input"
              type="password"
              className="form-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            data-testid="login-submit-button"
            className="btn btn-primary btn-lg w-full"
            disabled={loading}
          >
            <LogIn size={17} />
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <p className="login-footer">
          Accounts are created by your administrator.<br />
          Check your email for your setup link.
        </p>
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--color-navy);
          padding: 24px;
        }

        .login-card {
          width: 100%;
          max-width: 420px;
          background: white;
          border-radius: var(--radius-xl);
          padding: 36px;
          box-shadow: var(--shadow-lg);
        }

        .login-brand {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 28px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--color-border);
        }

        .login-logo {
          width: 52px;
          height: 52px;
          background: var(--color-navy);
          color: var(--color-green-light);
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .login-title {
          font-size: var(--text-lg);
          font-weight: 700;
          color: var(--color-text);
          line-height: 1.2;
        }

        .login-subtitle {
          font-size: var(--text-sm);
          color: var(--color-text-muted);
          margin-top: 2px;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: var(--sp-4);
        }

        .login-heading {
          font-size: var(--text-md);
          font-weight: 600;
          color: var(--color-text);
          margin-bottom: 4px;
        }

        .login-footer {
          margin-top: 20px;
          font-size: var(--text-xs);
          color: var(--color-text-muted);
          text-align: center;
          line-height: 1.6;
        }
      `}</style>
    </div>
  )
}
